import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";

const LINKS = [
  { label: "Home", hash: "#home" },
  { label: "About", hash: "#about" },
  { label: "Filmography", hash: "#filmography" },
  { label: "Services", route: "/services" },
  { label: "Film School", route: "/film-school" },
  { label: "Products", route: "/products" },
  { label: "Contact", hash: "#contact" },
];

export const AeroNav = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 50);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const go = (l) => {
    setOpen(false);
    if (l.route) {
      window.location.href = l.route;
      return;
    }
    if (pathname === "/") {
      document.querySelector(l.hash)?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      window.location.href = "/" + l.hash;
    }
  };

  // non-home pages: hamesha dark scrolled style (light pages par white text ke liye)
  const dark = scrolled || !isHome || open;

  return (
    <motion.header
      initial={{ y: -70, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        dark
          ? "bg-black/80 backdrop-blur-md border-b border-[#C9A227]/40"
          : "bg-transparent border-b border-transparent bg-gradient-to-b from-black/60 via-black/25 to-transparent"
      }`}
      data-testid="aero-nav"
    >
      <div className="max-w-[1500px] mx-auto px-6 md:px-10 h-[76px] flex items-center justify-between gap-6">
        <a href={isHome ? "#home" : "/"} data-testid="aero-nav-logo" className="leading-none shrink-0">
          <img src="/assets/logo-white.png" alt="FilmmakerAkki — Akhilesh Kumar Suman" className="h-12 w-auto drop-shadow-[0_2px_10px_rgba(0,0,0,0.5)]" />
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`aero-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => go(l)}
              className="font-mono text-[15px] font-semibold tracking-[0.08em] uppercase text-white/85 hover:text-white hover:drop-shadow-[0_0_12px_rgba(201,162,39,0.7)] transition-all duration-300"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            data-testid="aero-nav-book-shoot"
            href="/book-shoot"
            className="hidden sm:inline-flex items-center gap-2 bg-[#F5B301] text-black font-mono text-[13px] font-bold tracking-[0.14em] uppercase px-6 py-3 rounded-xl hover:bg-[#FFC42E] hover:-translate-y-0.5 transition-all duration-300"
          >
            Book a Shoot <ArrowUpRight size={13} />
          </a>
          <button
            data-testid="aero-nav-menu"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            className="lg:hidden w-11 h-11 flex items-center justify-center rounded-xl border border-white/25 text-white"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu — dark cinematic */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-black/95 backdrop-blur-xl border-b border-[#C9A227]/40"
            data-testid="aero-mobile-menu"
          >
            <div className="px-8 py-8 flex flex-col gap-6">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  data-testid={`aero-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  onClick={() => go(l)}
                  className="text-left font-mono text-[16px] font-semibold tracking-[0.1em] uppercase text-white/85 hover:text-[#F5B301] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                href="/book-shoot"
                data-testid="aero-mobile-book-shoot"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-[#F5B301] text-black font-mono text-[13px] font-bold tracking-[0.15em] uppercase px-6 py-4 rounded-xl hover:bg-[#FFC42E] transition-colors"
              >
                Book a Shoot <ArrowUpRight size={13} />
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
